import { Fragment, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { BET_LABEL, SPORT_META, formatOdds, bettingDayBounds } from "@/lib/picks";
import { fetchPickAccessMap } from "@/lib/access";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Skeleton } from "@/components/ui/skeleton";
import { CalendarIcon, X, ChevronDown, TrendingUp, TrendingDown, Minus, Sparkles, ShieldCheck, History as HistoryIcon, Filter, Layers } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import Disclaimer from "@/components/Disclaimer";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, ReferenceLine } from "recharts";

const PAGE_SIZE = 50;
const COLLAPSED_ROWS = 10;

type QuickRange = "7d" | "30d" | "all" | "custom";

export default function History() {
  const { user } = useAuth();
  const [picks, setPicks] = useState<any[]>([]);
  const [loadingPicks, setLoadingPicks] = useState(true);
  const [sport, setSport] = useState("all");
  const [bet, setBet] = useState("all");
  const [resultFilter, setResultFilter] = useState<"all" | "won" | "lost" | "pushed">("all");
  const [risk, setRisk] = useState<"all" | "low" | "medium" | "high">("all");
  const [confMin, setConfMin] = useState<string>("all"); // unit rating min
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [quickRange, setQuickRange] = useState<QuickRange>("all");
  const [page, setPage] = useState(0);
  const [stats, setStats] = useState<any>({ wins: 0, losses: 0, pushes: 0, units: 0, roi: 0, total_picks: 0 });
  const [trendPicks, setTrendPicks] = useState<any[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const dayBoundsFor = (ymd: string) => {
    const [y, m, d] = ymd.split("-").map(Number);
    const ref = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    return bettingDayBounds(ref);
  };

  // Apply quick range
  useEffect(() => {
    if (quickRange === "custom") return;
    if (quickRange === "all") { setFromDate(""); setToDate(""); return; }
    const days = quickRange === "7d" ? 7 : 30;
    const today = new Date();
    const from = new Date(today.getTime() - days * 24 * 60 * 60 * 1000);
    setFromDate(format(from, "yyyy-MM-dd"));
    setToDate(format(today, "yyyy-MM-dd"));
  }, [quickRange]);

  const riskFromUnits = (u: number) => (u <= 1 ? "low" : u <= 3 ? "medium" : "high");

  useEffect(() => {
    if (!user) return;
    setLoadingPicks(true);
    (async () => {
      const fromTs = fromDate ? dayBoundsFor(fromDate).start : undefined;
      const toTs = toDate ? dayBoundsFor(toDate).end : undefined;

      let query = supabase
        .from("picks")
        .select("*, sports(code)")
        .eq("status", "published")
        .is("deleted_at", null)
        .in("result", ["won", "lost", "pushed"])
        .order("published_at", { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      if (sport !== "all") query = query.eq("sports.code", sport as any);
      if (bet !== "all") query = query.eq("bet_type", bet as any);
      if (resultFilter !== "all") query = query.eq("result", resultFilter);
      if (fromTs) query = query.gte("published_at", fromTs);
      if (toTs) query = query.lt("published_at", toTs);

      const { data } = await query;
      let rows = (data ?? []) as any[];
      if (risk !== "all") rows = rows.filter((p) => riskFromUnits(Number(p.unit_rating ?? 0)) === risk);
      if (confMin !== "all") rows = rows.filter((p) => Number(p.unit_rating ?? 0) >= Number(confMin));

      const ids = rows.map((p) => p.id);
      const accessMap = await fetchPickAccessMap(user.id, fromTs, toTs, true, false, ids);
      setPicks(rows.map((p) => ({ ...p, access: accessMap.get(p.id) })));
      setLoadingPicks(false);
    })();
  }, [user?.id, sport, bet, resultFilter, risk, confMin, fromDate, toDate, page]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const fromTs = fromDate ? dayBoundsFor(fromDate).start : null;
      const toTs = toDate ? dayBoundsFor(toDate).end : null;
      const { data } = await supabase.rpc("record_stats", { _uid: user.id, _sport_code: sport === "all" ? null : sport, _bet_type: bet === "all" ? null : bet, _start_ts: fromTs, _end_ts: toTs });
      setStats(data?.[0] ?? {});
    })();
  }, [user?.id, sport, bet, fromDate, toDate]);

  // Trend chart: load up to 200 settled picks across all time for cumulative units
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("picks")
        .select("published_at, units_delta, result")
        .eq("status", "published")
        .is("deleted_at", null)
        .in("result", ["won", "lost", "pushed"])
        .order("published_at", { ascending: true })
        .limit(500);
      setTrendPicks(data ?? []);
    })();
  }, []);

  const trendData = useMemo(() => {
    // Aggregate by calendar day, then build cumulative running total per day.
    const byDay = new Map<string, number>();
    for (const p of trendPicks) {
      if (!p.published_at) continue;
      const day = format(new Date(p.published_at), "yyyy-MM-dd");
      byDay.set(day, (byDay.get(day) ?? 0) + Number(p.units_delta ?? 0));
    }
    const days = Array.from(byDay.keys()).sort();
    let cum = 0;
    return days.map((d) => {
      cum += byDay.get(d) ?? 0;
      return { date: `${d}T12:00:00`, units: Number(cum.toFixed(2)) };
    });
  }, [trendPicks]);

  const filtered = picks;
  const visiblePicks = showAll ? filtered : filtered.slice(0, COLLAPSED_ROWS);
  const hasMore = filtered.length > COLLAPSED_ROWS;
  const units = Number(stats.units ?? 0);
  const roi = Number(stats.roi ?? 0);

  return (
    <div className="container py-8 md:py-12">
      {/* Header */}
      <header className="flex flex-col gap-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
          <HistoryIcon className="h-3.5 w-3.5" />
          Performance Archive
        </div>
        <h1 className="font-display text-3xl md:text-5xl font-bold leading-tight">
          Performance <span className="te-grad-text">History</span>
        </h1>
        <p className="text-muted-foreground max-w-2xl text-sm md:text-base">
          Every settled pick remains permanently tracked for full transparency. No edits, no hidden losses — just the record.
        </p>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground/80">
          <ShieldCheck className="h-3.5 w-3.5 text-success" />
          Verified record · settled picks only
        </div>
      </header>

      {/* SECTION: Performance Summary */}
      <SectionHeader icon={<Sparkles className="h-4 w-4" />} eyebrow="01 — Summary" title="Performance Summary" subtitle="Lifetime metrics filtered by your selection." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mt-5">
        <BigStat label="Record" value={`${stats.wins ?? 0}-${stats.losses ?? 0}-${stats.pushes ?? 0}`} sub={`${stats.total_picks ?? 0} settled`} />
        <BigStat label="Units" value={`${units >= 0 ? "+" : ""}${units.toFixed(2)}u`} tone={units > 0 ? "pos" : units < 0 ? "neg" : "neu"} icon={units >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />} />
        <BigStat label="ROI" value={`${roi >= 0 ? "+" : ""}${roi.toFixed(1)}%`} tone={roi > 0 ? "pos" : roi < 0 ? "neg" : "neu"} />
        <BigStat label="Total Picks" value={`${stats.total_picks ?? 0}`} sub="lifetime tracked" />
      </div>

      {/* Trend chart */}
      <div className="te-card mt-4 p-4 md:p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Cumulative Units</div>
            <div className="text-sm text-foreground/80 mt-0.5">Long-term tracked performance · all settled picks</div>
          </div>
          <div className="text-xs text-muted-foreground">{trendData.length} pts</div>
        </div>
        <div className="h-44 md:h-56 mt-4">
          {trendData.length === 0 ? (
            <div className="h-full grid place-items-center text-xs text-muted-foreground">No trend data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendData} margin={{ top: 5, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="unitsFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tickFormatter={(v) => format(new Date(v), "MMM d")} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} minTickGap={32} />
                <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={36} />
                <ReferenceLine y={0} stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }}
                  labelFormatter={(v) => format(new Date(v), "MMM d, yyyy")}
                  formatter={(val: any) => [`${Number(val) >= 0 ? "+" : ""}${val}u`, "Units"]}
                />
                <Area type="monotone" dataKey="units" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#unitsFill)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* SECTION: Pick Log */}
      <SectionHeader icon={<Layers className="h-4 w-4" />} eyebrow="02 — Log" title="Pick Log" subtitle="Filter, scan, and inspect every settled play." />

      {/* Quick range tabs */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {([
          { k: "7d", l: "Last 7 days" },
          { k: "30d", l: "Last 30 days" },
          { k: "all", l: "All time" },
        ] as const).map((r) => (
          <button
            key={r.k}
            onClick={() => { setQuickRange(r.k); setPage(0); }}
            className={cn(
              "px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors",
              quickRange === r.k
                ? "border-primary/60 bg-primary/15 text-primary"
                : "border-border/60 bg-secondary/40 text-muted-foreground hover:text-foreground hover:bg-secondary/70",
            )}
          >
            {r.l}
          </button>
        ))}
        {quickRange === "custom" && (
          <span className="text-[11px] text-muted-foreground italic ml-1">custom range</span>
        )}
      </div>

      {/* Filters */}
      <div className="te-card p-3 md:p-4 mt-3">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-3">
          <Filter className="h-3.5 w-3.5" /> Filters
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <Select value={sport} onValueChange={(v) => { setSport(v); setPage(0); }}><SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Sport" /></SelectTrigger><SelectContent><SelectItem value="all">All sports</SelectItem>{Object.entries(SPORT_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.icon} {v.label}</SelectItem>)}</SelectContent></Select>
          <Select value={bet} onValueChange={(v) => { setBet(v); setPage(0); }}><SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Market" /></SelectTrigger><SelectContent><SelectItem value="all">All markets</SelectItem>{Object.entries(BET_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
          <Select value={resultFilter} onValueChange={(v) => { setResultFilter(v as any); setPage(0); }}><SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Result" /></SelectTrigger><SelectContent><SelectItem value="all">All results</SelectItem><SelectItem value="won">Won</SelectItem><SelectItem value="lost">Lost</SelectItem><SelectItem value="pushed">Push</SelectItem></SelectContent></Select>
          <Select value={risk} onValueChange={(v) => { setRisk(v as any); setPage(0); }}><SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Risk" /></SelectTrigger><SelectContent><SelectItem value="all">All risk</SelectItem><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent></Select>
          <Select value={confMin} onValueChange={(v) => { setConfMin(v); setPage(0); }}><SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Min units" /></SelectTrigger><SelectContent><SelectItem value="all">Any conf.</SelectItem><SelectItem value="1">≥ 1u</SelectItem><SelectItem value="2">≥ 2u</SelectItem><SelectItem value="3">≥ 3u</SelectItem><SelectItem value="4">≥ 4u</SelectItem><SelectItem value="5">≥ 5u</SelectItem></SelectContent></Select>
          <div className="grid grid-cols-2 gap-2 col-span-2 sm:col-span-1">
            <DateField label="From" value={fromDate} onChange={(v) => { setFromDate(v); setQuickRange("custom"); setPage(0); }} />
            <DateField label="To" value={toDate} onChange={(v) => { setToDate(v); setQuickRange("custom"); setPage(0); }} />
          </div>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden mt-5 space-y-3">
        {loadingPicks
          ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)
          : filtered.length === 0
          ? <EmptyState />
          : visiblePicks.map((p) => <MobilePickCard key={p.id} p={p} expanded={expanded === p.id} onToggle={() => setExpanded(expanded === p.id ? null : p.id)} />)}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block te-card mt-5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/30 text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr className="border-b border-border/60">
              <th className="text-left px-5 py-3 font-medium">Date</th>
              <th className="text-left px-3 py-3 font-medium">Pick</th>
              <th className="text-left px-3 py-3 font-medium">Sport</th>
              <th className="text-right px-3 py-3 font-medium">Odds</th>
              <th className="text-right px-3 py-3 font-medium">Units</th>
              <th className="text-center px-3 py-3 font-medium">Result</th>
              <th className="text-right px-3 py-3 font-medium">Δ Units</th>
              <th className="text-right px-5 py-3 font-medium" />
            </tr>
          </thead>
          <tbody>
            {loadingPicks ? (
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i} className="border-b border-border/40">
                  <td colSpan={8} className="p-3"><Skeleton className="h-10 w-full" /></td>
                </tr>
              ))
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="p-12"><EmptyState /></td></tr>
            ) : visiblePicks.map((p) => {
              const u = Number(p.units_delta ?? 0);
              const tint = p.result === "won" ? "hover:bg-success/[0.04]" : p.result === "lost" ? "hover:bg-danger/[0.04]" : "hover:bg-secondary/30";
              const isOpen = expanded === p.id;
              return (
                <Fragment key={p.id}>
                  <tr className={cn("border-b border-border/40 transition-colors", tint)}>
                    <td className="px-5 py-4 text-muted-foreground whitespace-nowrap text-xs">{p.published_at ? format(new Date(p.published_at), "MMM dd, yyyy") : "—"}</td>
                    <td className="px-3 py-4">
                      <div className="font-medium leading-tight">{p.access?.is_locked ? "🔒 Pro Pick" : p.title}</div>
                      <div className="text-[11px] text-muted-foreground/80 mt-0.5">{BET_LABEL[p.bet_type]}</div>
                    </td>
                    <td className="px-3 py-4 text-xs text-muted-foreground">
                      <span className="te-chip border border-border/50 bg-secondary/40 text-foreground/70 text-[10px]">
                        {SPORT_META[p.sports?.code]?.icon} {p.sports?.code}
                      </span>
                    </td>
                    <td className="px-3 py-4 text-right tabular-nums">{formatOdds(p.odds)}</td>
                    <td className="px-3 py-4 text-right tabular-nums text-xs text-muted-foreground">{p.unit_rating}u</td>
                    <td className="px-3 py-4 text-center"><ResultChip result={p.result} /></td>
                    <td className={cn("px-3 py-4 text-right font-bold tabular-nums text-base", u > 0 ? "text-success" : u < 0 ? "text-danger" : "text-muted-foreground")}>
                      {u > 0 ? "+" : ""}{u.toFixed(2)}u
                    </td>
                    <td className="px-5 py-4 text-right">
                      <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground hover:text-foreground" onClick={() => setExpanded(isOpen ? null : p.id)}>
                        Breakdown <ChevronDown className={cn("ml-1 h-3.5 w-3.5 transition-transform", isOpen && "rotate-180")} />
                      </Button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-border/40 bg-secondary/20">
                      <td colSpan={8} className="px-5 py-5">
                        <Breakdown p={p} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>


      {hasMore && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setShowAll((v) => !v); setExpanded(null); }}
            className="text-xs gap-1.5 border-border/60 bg-secondary/40 hover:bg-secondary/70"
          >
            {showAll ? "Show less" : `Show all ${filtered.length} picks`}
            <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", showAll && "rotate-180")} />
          </Button>
        </div>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</Button>
        <Button variant="secondary" size="sm" disabled={picks.length < PAGE_SIZE} onClick={() => setPage((p) => p + 1)}>Next</Button>
      </div>

      <ParlayHistory />

      <div className="mt-10"><Disclaimer compact /></div>
    </div>
  );
}

/* ---------- Subcomponents ---------- */

function SectionHeader({ icon, eyebrow, title, subtitle }: { icon: React.ReactNode; eyebrow: string; title: string; subtitle?: string }) {
  return (
    <div className="mt-10 md:mt-14 flex items-end justify-between gap-4 border-b border-border/40 pb-3">
      <div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          {icon}{eyebrow}
        </div>
        <h2 className="font-display text-xl md:text-2xl font-bold mt-1">{title}</h2>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}

function BigStat({ label, value, sub, tone, icon }: { label: string; value: string; sub?: string; tone?: "pos" | "neg" | "neu"; icon?: React.ReactNode }) {
  const valueClass = tone === "pos" ? "text-success" : tone === "neg" ? "text-danger" : "text-foreground";
  const ring = tone === "pos" ? "ring-success/20" : tone === "neg" ? "ring-danger/20" : "ring-border/40";
  return (
    <div className={cn("te-card p-5 md:p-6 ring-1 transition-all", ring)}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
        {icon && <div className={cn("opacity-70", valueClass)}>{icon}</div>}
      </div>
      <div className={cn("font-display text-3xl md:text-4xl font-bold mt-2 tabular-nums", valueClass)}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground/80 mt-1">{sub}</div>}
    </div>
  );
}

function ResultChip({ result }: { result: string }) {
  const cls =
    result === "won"
      ? "border-success/40 bg-success/15 text-success"
      : result === "lost"
      ? "border-danger/40 bg-danger/15 text-danger"
      : "border-muted-foreground/30 bg-muted/30 text-muted-foreground";
  const label = result === "won" ? "Won" : result === "lost" ? "Lost" : "Push";
  return (
    <span className={`inline-flex items-center justify-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider min-w-[52px] ${cls}`}>
      {label}
    </span>
  );
}

function MobilePickCard({ p, expanded, onToggle }: { p: any; expanded: boolean; onToggle: () => void }) {
  const u = Number(p.units_delta ?? 0);
  const tint = p.result === "won" ? "border-success/20" : p.result === "lost" ? "border-danger/20" : "border-border/60";
  return (
    <div className={cn("te-card p-4 border", tint)}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground">{p.published_at ? format(new Date(p.published_at), "MMM dd, yyyy") : "—"}</span>
        <ResultChip result={p.result} />
      </div>
      <div className="font-medium mt-2 leading-tight">{p.access?.is_locked ? "🔒 Pro Pick" : p.title}</div>
      <div className="flex items-center gap-2 mt-1.5 text-[11px] text-muted-foreground">
        <span>{SPORT_META[p.sports?.code]?.icon} {p.sports?.code}</span>
        <span className="opacity-50">·</span>
        <span>{BET_LABEL[p.bet_type]}</span>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
        <Mini label="Odds" value={formatOdds(p.odds)} />
        <Mini label="Units" value={`${p.unit_rating}u`} />
        <Mini label="Δ" value={`${u > 0 ? "+" : ""}${u.toFixed(2)}u`} tone={u > 0 ? "pos" : u < 0 ? "neg" : "neu"} bold />
      </div>
      <button onClick={onToggle} className="mt-3 w-full text-[11px] text-muted-foreground hover:text-foreground flex items-center justify-center gap-1 py-1.5 rounded-lg border border-border/40 bg-secondary/30">
        View Breakdown <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", expanded && "rotate-180")} />
      </button>
      {expanded && <div className="mt-3"><Breakdown p={p} /></div>}
    </div>
  );
}

function Mini({ label, value, tone, bold }: { label: string; value: string; tone?: "pos" | "neg" | "neu"; bold?: boolean }) {
  const cls = tone === "pos" ? "text-success" : tone === "neg" ? "text-danger" : "";
  return (
    <div>
      <div className="text-muted-foreground text-[10px] uppercase tracking-wider">{label}</div>
      <div className={cn("mt-0.5 tabular-nums", bold && "font-bold", cls)}>{value}</div>
    </div>
  );
}

function Breakdown({ p }: { p: any }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4 text-xs">
      <div className="space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Pick details</div>
        <Row k="Market" v={BET_LABEL[p.bet_type] ?? "—"} />
        <Row k="Sport" v={`${SPORT_META[p.sports?.code]?.icon ?? ""} ${p.sports?.code ?? "—"}`} />
        <Row k="Odds" v={formatOdds(p.odds)} />
        <Row k="Stake" v={`${p.unit_rating}u`} />
        <Row k="Settled" v={p.published_at ? format(new Date(p.published_at), "MMM dd, yyyy · HH:mm") : "—"} />
      </div>
      <div className="space-y-2 rounded-xl border border-border/40 bg-background/40 p-3">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Reasoning & analysis</div>
        <p className="text-muted-foreground/90 leading-relaxed whitespace-pre-wrap">
          {p.access?.is_locked
            ? "🔒 Upgrade to Pro to view detailed reasoning, line movement, and matchup analysis for this pick."
            : (p.ai_reasoning || p.notes || "No reasoning was recorded for this pick.")}
        </p>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-3 border-b border-border/30 pb-1.5">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-foreground/90 tabular-nums text-right">{v}</span>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-10">
      <div className="mx-auto h-10 w-10 rounded-full bg-secondary/60 grid place-items-center mb-3">
        <Minus className="h-5 w-5 text-muted-foreground" />
      </div>
      <div className="text-sm font-medium">No settled picks match these filters.</div>
      <div className="text-xs text-muted-foreground mt-1">Try widening your range or clearing filters.</div>
    </div>
  );
}

/* ---------- Parlay History ---------- */

function ParlayResultChip({ result }: { result: string }) {
  return <ResultChip result={result} />;
}

function ParlayHistory() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("parlay_of_day")
        .select("*")
        .eq("status", "published")
        .in("result", ["won", "lost", "pushed"])
        .order("betting_date", { ascending: false })
        .limit(50);
      setRows(data ?? []);
      setLoading(false);
    })();
  }, []);
  const wins = rows.filter((r) => r.result === "won").length;
  const losses = rows.filter((r) => r.result === "lost").length;
  const pushes = rows.filter((r) => r.result === "pushed").length;
  const units = rows.reduce((acc, r) => acc + Number(r.units_delta || 0), 0);

  return (
    <section className="mt-14 md:mt-20">
      <div className="flex items-end justify-between gap-4 border-b border-accent/20 pb-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-accent">
            <Sparkles className="h-3.5 w-3.5" /> 03 — High Variance
          </div>
          <h2 className="font-display text-xl md:text-2xl font-bold mt-1">Parlay History</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Tracked separately from straight picks. Parlays are inherently volatile — entertainment-first.
          </p>
        </div>
        <span className="te-chip border border-accent/30 bg-accent/10 text-accent text-[10px]">Bronze · Variance</span>
      </div>

      <div className="grid grid-cols-3 gap-3 md:gap-4 mt-5">
        <BigStat label="Record" value={`${wins}-${losses}-${pushes}`} sub={`${rows.length} settled`} />
        <BigStat label="Units" value={`${units >= 0 ? "+" : ""}${units.toFixed(2)}u`} tone={units > 0 ? "pos" : units < 0 ? "neg" : "neu"} />
        <BigStat label="Total" value={`${rows.length}`} sub="parlays settled" />
      </div>

      {/* Mobile */}
      <div className="md:hidden mt-5 space-y-3">
        {loading ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />) :
          rows.length === 0 ? <div className="te-card p-8 text-center text-sm text-muted-foreground">No concluded parlays yet.</div> :
          rows.map((r) => {
            const u = Number(r.units_delta ?? 0);
            return (
              <div key={r.id} className="te-card p-4 border border-accent/15">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] text-muted-foreground">{r.betting_date}</span>
                  <ParlayResultChip result={r.result} />
                </div>
                <div className="mt-2 font-medium leading-tight">{r.title}</div>
                <div className="grid grid-cols-4 gap-2 mt-3 text-xs">
                  <Mini label="Legs" value={`${r.leg_count}`} />
                  <Mini label="Odds" value={formatOdds(r.total_odds)} />
                  <Mini label="Units" value={`${r.suggested_units}u`} />
                  <Mini label="Δ" value={`${u > 0 ? "+" : ""}${u.toFixed(2)}u`} tone={u > 0 ? "pos" : u < 0 ? "neg" : "neu"} bold />
                </div>
              </div>
            );
          })}
      </div>

      {/* Desktop */}
      <div className="hidden md:block te-card mt-5 overflow-hidden border-accent/15">
        <table className="w-full text-sm">
          <thead className="bg-accent/[0.04] text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr className="border-b border-accent/15">
              <th className="text-left px-5 py-3 font-medium">Date</th>
              <th className="text-left px-3 py-3 font-medium">Title</th>
              <th className="text-right px-3 py-3 font-medium">Legs</th>
              <th className="text-right px-3 py-3 font-medium">Odds</th>
              <th className="text-right px-3 py-3 font-medium">Units</th>
              <th className="text-center px-3 py-3 font-medium">Result</th>
              <th className="text-right px-5 py-3 font-medium">Δ Units</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={i} className="border-b border-border/40"><td colSpan={7} className="p-3"><Skeleton className="h-10 w-full" /></td></tr>
              ))
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="p-10 text-center text-muted-foreground text-sm">No concluded parlays yet.</td></tr>
            ) : rows.map((r) => {
              const u = Number(r.units_delta ?? 0);
              const tint = r.result === "won" ? "hover:bg-success/[0.04]" : r.result === "lost" ? "hover:bg-danger/[0.04]" : "hover:bg-secondary/30";
              return (
                <tr key={r.id} className={cn("border-b border-border/40 transition-colors", tint)}>
                  <td className="px-5 py-4 text-muted-foreground whitespace-nowrap text-xs">{r.betting_date}</td>
                  <td className="px-3 py-4 font-medium">{r.title}</td>
                  <td className="px-3 py-4 text-right tabular-nums">{r.leg_count}</td>
                  <td className="px-3 py-4 text-right tabular-nums">{formatOdds(r.total_odds)}</td>
                  <td className="px-3 py-4 text-right tabular-nums text-xs text-muted-foreground">{r.suggested_units}u</td>
                  <td className="px-3 py-4 text-center"><ParlayResultChip result={r.result} /></td>
                  <td className={cn("px-5 py-4 text-right font-bold tabular-nums text-base", u > 0 ? "text-success" : u < 0 ? "text-danger" : "text-muted-foreground")}>
                    {u > 0 ? "+" : ""}{u.toFixed(2)}u
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DateField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  const date = value ? new Date(`${value}T00:00:00`) : undefined;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal h-9 text-xs bg-background/40 border-border/60 hover:bg-secondary/60 hover:text-foreground",
            !date && "text-muted-foreground",
          )}
        >
          <CalendarIcon className="mr-2 h-3.5 w-3.5 opacity-70" />
          <span className="flex-1 truncate">{date ? format(date, "MMM dd") : label}</span>
          {date && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(""); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onChange(""); } }}
              className="ml-1 rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-secondary"
              aria-label="Clear date"
            >
              <X className="h-3 w-3" />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => onChange(d ? format(d, "yyyy-MM-dd") : "")}
          initialFocus
          className={cn("p-3 pointer-events-auto")}
        />
      </PopoverContent>
    </Popover>
  );
}
